import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams, Switch, Route } from 'react-router-dom';
import enrichmentState from 'Stores/enrichment';
import crmIntegrationState from 'Stores/crmIntegration';
import SideBarNavigation from 'Components/common/sideBarNavigation';
import { LoadingWrapper } from 'Components/common/loadingWrapper';
import { ReactComponent as EnrichmentLogIcon } from 'Assets/svg/enrich-log.svg';
import { ReactComponent as SettingsIcon } from 'Assets/svg/settings.svg';
import { ReactComponent as TickIcon } from 'Assets/svg/tick-icon.svg';
import { PageNotFound } from 'Pages/pageNotFound/pageNotFound';
import integrationDataMap from 'Pages/integration/integrationDataMap';
import fieldMappingState from 'Stores/fieldMapping';
import { userDetail } from 'Stores/userDetail';
import { Utils } from 'Utils/utils';
import { EMPTY_FUNCTION, MX_ENRICHMENT_EVENTS } from 'Utils/constants';
import { FieldMapping } from 'Pages/integration/fieldMapping';
import FileEnrichment, { BackButton } from './fileEnrichment/fileEnrichment';
import EnrichmentLog from './enrichmentLog/enrichmentLog';
import EnrichmentLanding from './enrichmentLanding/enrichmentLanding';
import EmptyStateMessage from './emptyStateMessage';
import EnrichmentSettings from './enrichmentSettings';
import './_enrichment.scss';
import AutoEnrichmentInitiatedPopup from './autoEnrichmentPopup';

const getMenuData = crmType => {
  const showLeads =
    integrationDataMap[crmType.toUpperCase()]?.sublinks?.find(
      data => data.linkKey === 'field-mapping',
    )?.subMenu?.length === 2 || false;

  return [
    {
      titleText: 'Enrichment Log',
      linkKey: 'enrichment-log',
      linkValue: `/enrichment/${crmType}/enrichment-log/contacts`,
      isNavLink: true,
      icon: EnrichmentLogIcon,
      ...(showLeads && {
        subMenu: [
          {
            title: 'Contacts',
            link: `/enrichment/${crmType}/enrichment-log/contacts`,
            API_KEY: 'CONTACT',
          },
          {
            title: 'Leads',
            link: `/enrichment/${crmType}/enrichment-log/leads`,
            API_KEY: 'LEAD',
          },
        ],
      }),
    },
    {
      titleText: 'Settings',
      linkKey: 'settings',
      linkValue: '/enrichment/hubspot/settings',
      isNavLink: true,
      icon: SettingsIcon,
    },
  ];
};

const RenderSteps = props => {
  const { index, links, currentStep } = props;
  const stepCompleted = currentStep > index;

  return (
    <div
      className={`step-wrapper${currentStep === index ? ' active' : ''}${
        stepCompleted ? ' completed' : ''
      }`}
    >
      <div className="step-number">
        {stepCompleted ? (
          <TickIcon className="tick-icon" />
        ) : (
          <span>{index + 1}</span>
        )}
      </div>
      <div className="right-section">
        <div className="step-title">{`Step ${index + 1}`}</div>
        <div className="step-description">{links}</div>
      </div>
    </div>
  );
};

const CRMEnrichmentRoutes = observer(props => {
  const { crmType = '', menu = '', subMenu = '' } = useParams();
  const [currentStep, setCurrentStep] = useState(
    Utils.isNotUndefinedNotNull(userDetail.userInfo.hubspotEnrichmentSteps)
      ? Object.keys(userDetail.userInfo.hubspotEnrichmentSteps).findIndex(
          key => {
            return !userDetail.userInfo.hubspotEnrichmentSteps[key];
          },
        )
      : -1,
  );
  const [showLoader, setShowLoader] = useState(false);
  const [showEnrichmentInitiatedPopup, setShowEnrichmentPopup] = useState(
    false,
  );
  const [
    isEnrichmentSettingsFetched,
    setIsEnrichmentSettingsFetched,
  ] = useState(false);

  const { title, imgComponent, isSvg } = integrationDataMap[
    crmType.toUpperCase()
  ];

  const isConnected =
    crmIntegrationState.crmConnectionStatus[crmType.toUpperCase()];

  const showConnectionSteps =
    currentStep > -1 && userDetail.userInfo.isEnterpriseAdmin;

  const data = {
    title,
    imgComponent,
    isSvg,
    sublinks: showConnectionSteps
      ? ['Connect your CRM', 'Field Mapping', 'Settings']
      : getMenuData(crmType),
  };

  const getConnectedStatus = async () => {
    if (!crmIntegrationState.apiStat.getAllStatusFetched)
      await crmIntegrationState.getCrmConnectionDetails();

    if (!crmIntegrationState.crmConnectionStatus[crmType.toUpperCase()])
      setCurrentStep(0);

    if (crmIntegrationState.crmConnectionStatus[crmType.toUpperCase()]) {
      await fieldMappingState.getEnrichmentSettings(crmType.toUpperCase(), {
        notConnectedCallback: EMPTY_FUNCTION,
      });
      if (currentStep === 0) setCurrentStep(1);
    }
    setIsEnrichmentSettingsFetched(true);
  };

  const fieldMappingCallback = success => {
    if (success) {
      enrichmentState.setEnrichmentStepsCompleted({
        CONNECT: true,
        FIELD_MAPPING: true,
      });
      setCurrentStep(2);
    }
  };

  const enrichmentSettingsCallback = success => {
    if (success) {
      enrichmentState.setEnrichmentStepsCompleted({ SETTINGS: true });
      setShowEnrichmentPopup(true);
    }
  };

  useEffect(() => {
    if ([0, 1, 2].includes(currentStep)) {
      let eventName = '';
      if (currentStep === 0)
        eventName = MX_ENRICHMENT_EVENTS.CRM_ENRICHMENT_CONNECT_STEP_SHOWN;
      else if (currentStep === 1)
        eventName = MX_ENRICHMENT_EVENTS.CRM_ENRICHMENT_MAPPING_STEP_SHOWN;
      else eventName = MX_ENRICHMENT_EVENTS.CRM_ENRICHMENT_SETTINGS_STEP_SHOWN;

      Utils.mixpanelTrack(eventName, { type: crmType });
    }
  }, [currentStep]);

  React.useEffect(() => {
    getConnectedStatus();
  }, []);

  return (
    <>
      <LoadingWrapper
        show={showLoader || fieldMappingState.isLoading}
        position="fixed"
      />
      {showEnrichmentInitiatedPopup && (
        <AutoEnrichmentInitiatedPopup
          togglePopup={() => {
            setShowEnrichmentPopup(false);
            setCurrentStep(-1);
          }}
        />
      )}
      <div
        className={`section-wrap${isConnected ? '' : ' disable'} ${menu}-view`}
      >
        <div className="left-section">
          <BackButton title={`${title} Enrichment`} />
          <SideBarNavigation
            expandFirstMenu={!isConnected}
            collapseInactiveMenu
            data={data}
            customRenderList={
              showConnectionSteps
                ? listProps => {
                    return (
                      <RenderSteps
                        key={listProps.links}
                        {...listProps}
                        currentStep={currentStep}
                      />
                    );
                  }
                : null
            }
            activeMenu={isConnected ? menu : ''}
            selectedSubMenu={isConnected ? subMenu : ''}
            connectionStatus={isConnected ? 'Connected' : 'Not Connected'}
          />
        </div>
        <div className="main-section">
          {((showConnectionSteps && currentStep === 0) || !isConnected) && (
            <EmptyStateMessage
              title="Connect your CRM"
              description={`Sync your ${title} CRM with Adapt and start enriching your records.`}
              linkText="Connect Now"
              crmLogoData={data}
              crmType={crmType}
              setShowLoader={setShowLoader}
              setCurrentStep={setCurrentStep}
            />
          )}
          {showConnectionSteps && currentStep === 1 && isConnected && (
            <FieldMapping callback={fieldMappingCallback} alwaysEnableButton />
          )}
          {showConnectionSteps && currentStep === 2 && isConnected && (
            <EnrichmentSettings
              callback={enrichmentSettingsCallback}
              animateSwitch
              showToast={false}
              enableEdit
              isEnrichmentSettingsFetched={isEnrichmentSettingsFetched}
            />
          )}
          {isConnected && !showConnectionSteps && (
            <Switch>
              <Route
                exact
                path="/enrichment/:crmType/enrichment-log/:subMenu"
                render={routeProps => {
                  return <EnrichmentLog {...routeProps} />;
                }}
              />
              <Route
                exact
                path="/enrichment/:crmType/settings"
                render={routeProps => {
                  return (
                    <EnrichmentSettings
                      {...routeProps}
                      isEnrichmentSettingsFetched={isEnrichmentSettingsFetched}
                    />
                  );
                }}
              />
            </Switch>
          )}
        </div>
      </div>
    </>
  );
});

const Enrichment = observer(() => {
  return (
    <div className="enrichment-page">
      <Switch>
        <Route exact path="/enrichment" component={EnrichmentLanding} />
        <Route
          path="/enrichment/:crmType(hubspot)/:menu(enrichment-log|settings)/:subMenu(contacts|leads)?"
          component={CRMEnrichmentRoutes}
        />
        <Route exact path="/enrichment/file" component={FileEnrichment} />
        <Route path="/*" component={PageNotFound} />
      </Switch>
      <LoadingWrapper show={enrichmentState.isLoading} position="fixed" />
    </div>
  );
});

export default Enrichment;
